/**
 * QuestionEditModal Component
 * Modal for editing question text and helper text
 */

import React, { useEffect } from 'react';
import { Modal, Form, Input } from 'antd';
import { IFlowQuestion } from '../../utils/jsonFlowGenerator';
import { QuestionCustomization } from '../../hooks/useChatbotFlow';

const { TextArea } = Input;

interface QuestionEditModalProps {
  open: boolean;
  question: IFlowQuestion | null;
  customization?: QuestionCustomization;
  onSave: (questionId: string, customization: Partial<QuestionCustomization>) => void;
  onCancel: () => void;
}

export const QuestionEditModal: React.FC<QuestionEditModalProps> = ({
  open,
  question,
  customization,
  onSave,
  onCancel,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (question && open) {
      form.setFieldsValue({
        questionText: customization?.questionText || question.question,
        helperText: customization?.helperText || '',
      });
    }
  }, [question, customization, open, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (question) {
        const customizationUpdate: Partial<QuestionCustomization> = {};

        // Only save questionText if it differs from the original
        if (values.questionText !== question.question) {
          customizationUpdate.questionText = values.questionText;
        } else {
          customizationUpdate.questionText = undefined;
        }

        // Save helperText if it has a value
        if (values.helperText && values.helperText.trim()) {
          customizationUpdate.helperText = values.helperText.trim();
        } else {
          customizationUpdate.helperText = undefined;
        }

        onSave(question.questionId, customizationUpdate);
      }
    } catch (error) {
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  if (!question) return null;

  return (
    <Modal
      title={`Edit Question: ${question.questionId.split('_')[0]}`}
      open={open}
      onOk={handleSave}
      onCancel={handleCancel}
      width={500}
      okText="Save Changes"
      cancelText="Cancel"
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          label="Question Text"
          name="questionText"
          rules={[{ required: true, message: 'Question text is required' }]}
        >
          <TextArea
            rows={3}
            placeholder="Enter the question text"
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item
          label="Helper Text (Optional)"
          name="helperText"
        >
          <TextArea
            rows={2}
            placeholder="Enter optional helper text for this question"
            showCount
            maxLength={300}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
